/**
 * @file     NewsReadBlock.tsx
 * @purpose  Renders a news_read block: Relta's structured take on one external
 *           article — verdict lead, a Read/Skim/Skip call, key points with our
 *           reaction, a BC-angle callout, and a source card linking back.
 */
'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useAppTheme } from '@/core/theme/useAppTheme';
import type { ContentBlock } from '@/services/content-engine/types';

type NewsReadBlockProps = Extract<ContentBlock, { t: 'news_read' }>;

const ExternalIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 5h5v5M19 5l-8 8M18 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h4" />
  </svg>
);

const READ_LABEL: Record<NewsReadBlockProps['worthReading'], string> = {
  read: 'Worth a read',
  skim: 'Worth a skim',
  skip: 'Skip it',
};

export const NewsReadBlock = ({
  verdict,
  worthReading,
  worthReadingNote,
  points,
  bcAngle,
  sourceQuote,
  source,
}: NewsReadBlockProps) => {
  const { theme } = useAppTheme();
  const chipColor =
    worthReading === 'read'
      ? theme.semanticColors.interactivePrimary
      : worthReading === 'skim'
        ? theme.semanticColors.textSecondary
        : theme.semanticColors.textMuted;

  return (
    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
      {/* Verdict + the call */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: '8px',
            px: '10px',
            py: '5px',
            borderRadius: `${theme.layout.radius.pill}px`,
            border: `1px solid ${chipColor}`,
            color: chipColor,
          }}
        >
          <Box
            component="span"
            sx={{ width: 6, height: 6, borderRadius: `${theme.layout.radius.pill}px`, backgroundColor: chipColor }}
          />
          <Typography
            component="span"
            sx={{
              fontSize: theme.typography.caption,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {READ_LABEL[worthReading]}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: theme.typography.fontFamilyHeading,
            fontSize: theme.typography.h4,
            fontWeight: 600,
            lineHeight: 1.3,
            color: theme.semanticColors.textPrimary,
            textWrap: 'balance',
          }}
        >
          {verdict}
        </Typography>
        {worthReadingNote ? (
          <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textSecondary }}>
            {worthReadingNote}
          </Typography>
        ) : null}
      </Box>

      {/* Key points, each with our reaction */}
      {points.length > 0 ? (
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: '18px' }}>
          {points.map((p, i) => (
            <Box
              component="li"
              key={i}
              sx={{ pl: '16px', borderLeft: `2px solid ${theme.semanticColors.borderStrong}` }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.bodyLg,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  color: theme.semanticColors.textPrimary,
                  mb: '6px',
                }}
              >
                {p.point}
              </Typography>
              <Typography sx={{ fontSize: theme.typography.bodySm, lineHeight: 1.6, color: theme.semanticColors.textSecondary }}>
                {p.take}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}

      {/* Optional pulled quote */}
      {sourceQuote ? (
        <Typography
          component="blockquote"
          sx={{
            m: 0,
            pl: '18px',
            borderLeft: `3px solid ${theme.semanticColors.interactivePrimary}`,
            fontFamily: theme.typography.fontFamilyHeading,
            fontSize: theme.typography.bodyLg,
            fontStyle: 'italic',
            lineHeight: 1.5,
            color: theme.semanticColors.textPrimary,
          }}
        >
          &ldquo;{sourceQuote}&rdquo;
        </Typography>
      ) : null}

      {/* BC angle callout */}
      {bcAngle ? (
        <Box
          sx={{
            p: '18px',
            borderRadius: `${theme.layout.radius.lg}px`,
            border: `1px solid ${theme.semanticColors.borderSubtle}`,
            backgroundColor: theme.semanticColors.surfaceElevated,
          }}
        >
          <Typography
            sx={{
              fontSize: theme.typography.caption,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: theme.semanticColors.textMuted,
              mb: '8px',
            }}
          >
            What this means in BC
          </Typography>
          <Typography sx={{ fontSize: theme.typography.bodySm, lineHeight: 1.6, color: theme.semanticColors.textPrimary }}>
            {bcAngle}
          </Typography>
        </Box>
      ) : null}

      {/* Source card + backlink */}
      <Box
        component="a"
        href={source.url}
        target="_blank"
        rel="nofollow noopener noreferrer"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          alignSelf: 'flex-start',
          gap: '8px',
          minHeight: 44,
          textDecoration: 'none',
          color: theme.semanticColors.textSecondary,
          fontSize: theme.typography.bodySm,
          '&:hover': { color: theme.semanticColors.interactivePrimary },
          '&:focus-visible': {
            outline: `2px solid ${theme.semanticColors.interactiveFocusRing}`,
            outlineOffset: '2px',
          },
        }}
      >
        <span>
          First reported by <strong>{source.publisher}</strong>
        </span>
        <ExternalIcon />
      </Box>
    </Box>
  );
};
